import "normalize.css";

import { library } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import {
  faArrowLeft,
  faArrowRight,
  faArrowRotateBack,
  faArrowsRotate,
  faBookOpen,
  faCamera,
  faCheck,
  faChevronLeft,
  faChevronRight,
  faCircleExclamation,
  faCircleInfo,
  faCirclePlay,
  faCircleStop,
  faDownload,
  faExclamationTriangle,
  faFloppyDisk,
  faLightbulb,
  faLock,
  faMagnifyingGlass,
  faMinus,
  faMusic,
  faPause,
  faPencil,
  faPlay,
  faPlus,
  faPrint,
  faRepeat,
  faRightFromBracket,
  faStar,
  faStop,
  faTrash,
  faVolumeHigh,
  faVolumeXmark,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { faCirclePlay as faRegularCirclePlay } from "@fortawesome/free-regular-svg-icons";
import * as Sentry from "@sentry/vue";
import { createHead } from "@unhead/vue";
import {
  addResponseHandler,
  type ResponseHandlerParams,
  setGlobalRequestHeader,
} from "@virgodev/bazaar/functions/api";
import Cookies from "js-cookie";
import { createApp } from "vue";
import { createPinia } from "pinia";

import App from "./App.vue";
import router, { CHUNK_LOAD_ERROR_MESSAGES } from "./router";
import { frontEndOnlyModeIsOn } from "@/front_end_only_mode";
import { installFakeApi } from "@/front_end_only_mode/fake_api";
import { useStore } from "@/stores";

if (frontEndOnlyModeIsOn()) {
  installFakeApi();
}

library.add(faArrowLeft);
library.add(faArrowRight);
library.add(faArrowRotateBack);
library.add(faArrowsRotate);
library.add(faBookOpen);
library.add(faCamera);
library.add(faCheck);
library.add(faChevronLeft);
library.add(faChevronRight);
library.add(faCircleExclamation);
library.add(faCircleInfo);
library.add(faCirclePlay);
library.add(faCircleStop);
library.add(faDownload);
library.add(faExclamationTriangle);
library.add(faFloppyDisk);
library.add(faLightbulb);
library.add(faLock);
library.add(faMagnifyingGlass);
library.add(faMinus);
library.add(faMusic);
library.add(faPause);
library.add(faPencil);
library.add(faPlay);
library.add(faPlus);
library.add(faPrint);
library.add(faRepeat);
library.add(faRightFromBracket);
library.add(faStar);
library.add(faStop);
library.add(faTrash);
library.add(faVolumeHigh);
library.add(faVolumeXmark);
library.add(faXmark);
library.add(faRegularCirclePlay);

if (navigator.userAgent.includes("Mac OS X")) {
  document.documentElement.classList.add("is-apple-device");
}

addResponseHandler("all", () => {
  setGlobalRequestHeader("X-CSRFToken", Cookies.get("csrftoken"));
});

addResponseHandler(403, ({ body }: ResponseHandlerParams) => {
  if (
    ["school_teacher", "school_student"].includes(
      useStore().userData.user_type,
    ) &&
    [
      "You do not have an active subscription.",
      "You are not a school teacher or your district is inactive.",
    ].includes(body.detail)
  ) {
    router.replace({
      name: "admin_area",
      query: { inactive: "true" },
    });
  } else if (body.detail === "You do not have an active subscription.") {
    router.replace({
      name: "my_account",
      query: { no_active_subscription: "true" },
    });
  } else {
    useStore().logOut();

    if (router.currentRoute.value.name !== "login") {
      router.push({
        name: "login",
        query: { next: router.currentRoute.value.fullPath },
      });
    }
  }
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
addResponseHandler(404, ({ body }: { body: any }) => {
  if (
    typeof body === "object" &&
    body.detail === "Student not found." &&
    router.currentRoute.value.name !== "choose_student"
  ) {
    router.push({
      name: "choose_student",
      query: { next: router.currentRoute.value.fullPath },
    });
  }
});

const app = createApp(App);

if (import.meta.env.MODE === "production") {
  Sentry.init({
    app,
    dsn: "https://ff133bd75f3d459d90d3fc4f40e7cee1@errors2.danemco.net/4",
    integrations: [
      new Sentry.BrowserTracing({
        routingInstrumentation: Sentry.vueRouterInstrumentation(router),
      }),
    ],
    tracesSampleRate: 1.0,
    ignoreErrors: CHUNK_LOAD_ERROR_MESSAGES,
    denyUrls: [/^https:\/\/cdn\.amplitude\.com\//],
  });
}

app.component("FontAwesomeIcon", FontAwesomeIcon);

app.use(createPinia());
app.use(router);
app.use(createHead());

app.mount("#app");
